import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

export default function Register() {
  const { loginWith } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      loginWith(data.token, data.user);
      toast.success("Account created");
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 py-24">
      <div className="w-full max-w-md fadeup">
        <Link to="/" className="flex items-center justify-center mb-8">
          <img src="/ai-logo.jpg" alt="AI ID Card" className="h-28 w-auto object-contain logo-neon" />
        </Link>

        <div className="border border-zinc-800 bg-[#090A0C] p-8">
          <p className="font-mono-p text-xs tracking-[0.3em] uppercase text-cyan-400 mb-2">
            // Create Account
          </p>
          <h1 className="font-mono-p font-bold uppercase tracking-tight text-2xl mb-2">
            Register — Free
          </h1>
          <p className="text-sm text-zinc-500 mb-8">
            Sign up free. Purchase a plan afterwards to unlock your AI ID Cards.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="font-mono-p text-xs uppercase tracking-widest text-zinc-500">Name</label>
              <input
                data-testid="register-name"
                required
                value={form.name}
                onChange={set("name")}
                className="mt-2 w-full bg-black border border-zinc-800 px-4 py-3 text-zinc-100 text-sm focus:border-cyan-400/60 focus:outline-none focus:ring-1 focus:ring-cyan-400/30 transition-colors"
              />
            </div>
            <div>
              <label className="font-mono-p text-xs uppercase tracking-widest text-zinc-500">Email</label>
              <input
                data-testid="register-email"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                className="mt-2 w-full bg-black border border-zinc-800 px-4 py-3 text-zinc-100 text-sm focus:border-cyan-400/60 focus:outline-none focus:ring-1 focus:ring-cyan-400/30 transition-colors"
              />
            </div>
            <div>
              <label className="font-mono-p text-xs uppercase tracking-widest text-zinc-500">Password</label>
              <input
                data-testid="register-password"
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={set("password")}
                className="mt-2 w-full bg-black border border-zinc-800 px-4 py-3 text-zinc-100 text-sm focus:border-cyan-400/60 focus:outline-none focus:ring-1 focus:ring-cyan-400/30 transition-colors"
              />
            </div>
            <button
              data-testid="register-submit"
              disabled={loading}
              className="w-full font-mono-p text-sm uppercase tracking-widest bg-cyan-400 text-black py-3.5 font-bold hover:bg-cyan-300 transition-colors disabled:opacity-50"
            >
              {loading ? "Creating..." : "Create Free Account"}
            </button>
          </form>
        </div>

        <p className="text-center mt-6 text-sm text-zinc-500">
          Already have an account?{" "}
          <Link to="/login" data-testid="register-to-login" className="text-cyan-400 hover:underline">
            Login
          </Link>
        </p>
      </div>
    </div>
  );
}
